// @ts-check
// pwa/components/tab-nav.js — universal 4-tab navigation (spec 003).
//
// Contract (docs/UNIVERSAL_HEADER.md):
//   item 3 — every nav link is a plain <a href> with a data-nav attribute
//            (pwa/lib/router.js intercepts the click; no onclick handlers)
//   item 4 — the Admin tab carries [data-admin-only]; revealed by
//            body[data-is-admin="true"] (app-header.css)
//   item 6 — active tab via aria-current="page" + aria-[current=page]: utilities
//            (NOT an .is-active class). setActiveRoute() flips aria-current on
//            navigation; this template sets it for the initial paint.
//
// Render order: Dashboard · Sign up · Resources (disabled) · Admin (gated).
// Both the desktop centred nav and the mobile second-bar share this template;
// CSS toggles which is visible. Admins LAND on Dashboard, not Admin (privacy
// decision, brief § 5.1) — the Admin tab is opt-in.

import { html } from 'lit-html';
import { unsafeHTML } from 'lit-html/directives/unsafe-html.js';
import { icon } from '../lib/icons.js';

const TABS = [
  { href: '/',          nav: 'dashboard', label: 'Dashboard', icon: 'layout-dashboard' },
  { href: '/signup',    nav: 'signup',    label: 'Sign up',   icon: 'calendar-search' },
  { href: '/resources', nav: 'resources', label: 'Resources', icon: 'library' },
];

// Desktop active = white pill via Tailwind aria-[current=page]: utilities.
// The mobile footer styles its active tab in CSS (Sand-Yellow icon+label, no
// pill), so the mobile variant OMITS those utilities — otherwise they'd win by
// @layer ordering and paint a full-width white block behind the footer tab.
const ACTIVE_DESKTOP = 'on-dark aria-[current=page]:bg-white aria-[current=page]:text-nc-ocean';
const ACTIVE_MOBILE = 'on-dark';

/**
 * @param {{ pathname: string, variant?: 'desktop'|'mobile' }} opts
 * @returns {import('lit-html').TemplateResult}
 */
export function tabNavTemplate({ pathname, variant = 'desktop' }) {
  const cls = variant === 'mobile' ? 'tabnav tabnav-mobile' : 'tabnav tabnav-desktop';
  const activeCls = variant === 'mobile' ? ACTIVE_MOBILE : ACTIVE_DESKTOP;
  const isActive = (href) => href === pathname;
  // Label wrapped in a span so the mobile footer can stack it under the icon
  // (icon = row 1, .tabnav__label = row 2); inline + harmless on desktop.
  return html`
    <nav class=${cls} role="tablist" aria-label=${variant === 'mobile' ? 'Hub navigation (mobile)' : 'Hub navigation'}>
      ${TABS.map((t) => html`
        <a href=${t.href} data-nav=${t.nav} role="tab"
           aria-current=${isActive(t.href) ? 'page' : null}
           class=${activeCls}>
          ${unsafeHTML(icon(t.icon, 'w-3.5 h-3.5'))} <span class="tabnav__label">${t.label}</span>
        </a>`)}
      <a href="/youth-board/" role="tab" data-yb-only rel="external"
         aria-current=${isActive('/youth-board/') ? 'page' : null}
         class=${activeCls}>
        ${unsafeHTML(icon('users-round', 'w-3.5 h-3.5'))} <span class="tabnav__label">Youth Board</span>
      </a>
      <a href="/admin" data-nav="admin" role="tab" data-admin-only data-tip="dash-admin"
         aria-current=${isActive('/admin') ? 'page' : null}
         class=${activeCls}>
        ${unsafeHTML(icon('shield', 'w-3.5 h-3.5'))} <span class="tabnav__label">Admin</span>
      </a>
    </nav>
  `;
}
